import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { ProviderStateService } from '../services/provider-state.service';
import { GraphqlService } from '../services/graphql.service';
import { ProviderData } from '../models/provider';

@Injectable({
  providedIn: 'root'
})
export class VerifyProviderGuard implements CanActivate {

  private readonly defaultSlug = 'eglobaldoctors';
  constructor(
    private router: Router,
    private readonly graphqlService: GraphqlService,
    private providerStateService: ProviderStateService
  ) {}

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    const slugFromQuery = next.paramMap.get('slug'); // Get slug from URL parameters
    const existingProviderData = this.providerStateService.getProviderData(); // Get existing provider data from service
    const slug = slugFromQuery || (existingProviderData ? existingProviderData.slug : this.defaultSlug); // Use slug from URL if provided, otherwise from existing data or default

    console.log('slugFromQuery:', slugFromQuery);
    console.log('Existing Provider Data:', existingProviderData);
    console.log('Resolved Slug:', slug);

    // Check if existing data matches the requested slug
    if (existingProviderData && existingProviderData.slug === slug) {
      console.log('Using existing provider data');
      return of(true); // Return true immediately if data is already available
    }

    // Fetch provider data from GraphQL service
    return this.graphqlService.findProvider(slug).pipe(
      switchMap(result => {
        const providerId = result?.id;
        console.log('Find Provider Result:', result);
        if (providerId) {
          // Fetch metadata for the provider
          return this.graphqlService.fetchProviderMetadata(providerId).pipe(
            map(metadata => {
              if (metadata && metadata.provider && metadata.metadata && metadata.metadata.images) {
                const providerData: ProviderData = {
                  providerId: providerId,
                  isPrimaryProvider: metadata.provider.is_primary_provider,
                  name: metadata.provider.name,
                  logoUrl: metadata.metadata.images.logo.default,
                  slug: slug
                };
                console.log('Setting provider data:', providerData);
                this.providerStateService.setProviderData(providerData);
                return true; // Return true indicating successful activation
              } else {
                console.error('Incomplete or missing metadata:', metadata);
                this.router.navigate(['/notfound']); // Navigate to 404 page if metadata is incomplete
                return false; // Return false indicating failed activation
              }
            }),
            catchError((error) => {
              console.error('Error fetching provider metadata:', error);
              this.router.navigate(['/notfound']); // Handle errors fetching metadata
              return of(false); // Return false indicating failed activation
            })
          );
        } else {
          console.error('Provider ID mismatch or provider not found:', result);
          this.router.navigate(['/notfound']); // Navigate to 404 page if provider ID mismatch or not found
          return of(false); // Return false indicating failed activation
        }
      }),
      catchError((error) => {
        console.error('Error finding provider:', error);
        this.router.navigate(['/notfound']); // Handle errors finding provider
        return of(false); // Return false indicating failed activation
      }),
      tap(canActivate => {
        if (!canActivate) {
          this.router.navigate(['/notfound']); // Navigate to 404 page if canActivate is false
        }
      })
    );
  }
}
